import styled, { keyframes } from "styled-components";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { DEVICE_DIMENSIONS } from "@dineout-payment-success-ui/constants";

const TOAST_BOTTOM_MARGIN = "100px";
const TOAST_CONTAINER_CLASSNAME = "toast";
const HEADER_HEIGHT = "64px";
const HELP_ICON_BORDER_COLOR = "#D9DADB";
const MAX_WIDTH = "500px";
const TABLET_MAX_WIDTH = "700px";

const PAID_IMAGE_WRAPPER_DIMENSIONS = {
    height: "161px",
    width: "161px",
    right: "-13px",
    top: "-25px",
};

export interface IHeaderContainerProps {
    isAndroid: boolean;
}

export const DinecashPoolBanner = styled(Image)(({ theme }) => ({
    padding: `${theme.spacing.large} ${theme.spacing.medium} 0`,
}));

export const DineoutWrapper = styled.div(({ theme }) => ({
    overflowY: "scroll",
    background: theme.colors[theme.colorMode].canvas_Primary,
    height: "100vh",
    "&::-webkit-scrollbar": {
        display: "none",
    },
    "-ms-overflow-style": "none", // IE and Edge
    "scrollbar-width": "none", // Firefox
    "-webkit-overflow-scrolling": "touch", // Smooth scrolling for iOS
    "@supports (padding-top: var(--sat, 0px))": {
        paddingTop: "var(--sat, 0px)",
    },
    "@supports (padding-bottom: var(--sab))": {
        marginBottom: "var(--sab)",
    },
    [`@media (min-width: ${DEVICE_DIMENSIONS.DESKTOP_MIN})`]: {
        maxWidth: "100%",
    },
}));

export const BgContainer = styled.div<{ customHeight?: number }>(({ customHeight }) => ({
    height: customHeight || 450,
    width: "100%",
    maxWidth: MAX_WIDTH,
    margin: "0 auto",
}));

export const ScrollableContent = styled.div<{
    skipTopPadding?: boolean;
    isApp?: boolean;
    skipHorizontalPadding?: boolean;
}>(({ theme, skipTopPadding, isApp = true, skipHorizontalPadding = false }) => ({
    padding: skipHorizontalPadding ? `${theme.spacing.medium} 0px` : `${theme.spacing.medium}`,
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    ...(isApp &&
        !skipTopPadding && {
            "@supports (padding-top: var(--sat, 0px))": {
                paddingTop: "var(--sat, 0px)",
            },
        }),
}));

interface IPaidImageWrapperProps {
    isSavingsAvailable: boolean;
    isCalloutExists: boolean;
    isSavingCardsAvailable: boolean;
    isSplitBillWidgetAvailable?: boolean;
}

export const getMarginTopValue = ({
    isCalloutExists,
    isSavingsAvailable,
    isSplitBillWidgetAvailable,
}: IPaidImageWrapperProps) => {
    if (!isCalloutExists) {
        if (isSplitBillWidgetAvailable) {
            return "14px";
        }
        return isSavingsAvailable ? "20px" : "30px";
    }
    return "0px";
};

export const BillDetailsWrapper = styled.div<IPaidImageWrapperProps>(
    ({ isCalloutExists, isSavingsAvailable, isSavingCardsAvailable, isSplitBillWidgetAvailable }) => ({
        marginTop: getMarginTopValue({
            isCalloutExists,
            isSavingsAvailable,
            isSavingCardsAvailable,
            isSplitBillWidgetAvailable,
        }),
        marginBottom: "150px",
        position: "relative",
    }),
);

export const PaidImageWrapper = styled.div<IPaidImageWrapperProps>(() => ({
    position: "absolute",
    height: PAID_IMAGE_WRAPPER_DIMENSIONS.height,
    width: PAID_IMAGE_WRAPPER_DIMENSIONS.width,
    right: PAID_IMAGE_WRAPPER_DIMENSIONS.right,
    top: PAID_IMAGE_WRAPPER_DIMENSIONS.top,
    zIndex: 1,
}));

export const slideAnimation = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
`;

export const AnimatedScrollableContent = styled.div`
    animation: ${slideAnimation} 0.4s ease-in-out;
`;

interface IHeaderWrapperProps {
    isScrolled?: boolean;
}

interface IOneBillWrapper {
    isMarginTop: boolean;
}
export const OneAndBillWrapper = styled.div<IOneBillWrapper>`
    margin-top: ${(props) => (props.isMarginTop ? "-115px" : "-130px")};
`;

export const SplitBillWidgetAndImageWrapper = styled.div(({ theme }) => ({
    position: "relative",
    marginBottom: theme.spacing.x11,
}));

export const ContentDiv = styled.div`
    position: "relative";
    margin-bottom: 170px;
`;

export const HeaderContainer = styled.div<IHeaderContainerProps>(({ isAndroid, theme }) => ({
    height: "500px",
    marginTop: isAndroid ? theme.spacing.xxLarge : undefined,
}));

export const BillWrapper = styled.div<{ showHorizontalMargin?: boolean }>(
    ({ theme, showHorizontalMargin = false }) => ({
        margin: `${theme.spacing.xxLarge} ${showHorizontalMargin ? theme.spacing.medium : 0}`,
        marginBottom: theme.spacing.large,
    }),
);

export const ListViewWrapper = styled.div(({ theme }) => ({
    margin: `${theme.spacing.large} ${theme.spacing.medium} 0`,

    "& > div": {
        "& > div": {
            "& > div": {
                marginBottom: 0,
            },
        },
    },
    "& > div > div:last-child": {
        borderBottom: `2px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    },
}));

export const ListTextWrapper = styled(Text)(({ theme }) => ({
    "& > ul": {
        padding: `${theme.spacing.xxSmall} ${theme.spacing.medium}`,
        paddingBottom: 0,
        listStyleType: "disc",
    },
    "& > ol": {
        padding: `${theme.spacing.xxSmall} ${theme.spacing.medium}`,
        paddingBottom: 0,
        listStyleType: "decimal",
    },
}));

export const HeaderTicketWrapper = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.small,
}));

export const TicketDetailsWrapper = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xLarge,
    margin: `${theme.spacing.xxLarge} ${theme.spacing.medium} 0 `,
}));

export const StealDealOrderDetailsWrapper = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xLarge,
    margin: `0 ${theme.spacing.medium} 0 `,
}));

export const InfoTicketWrapper = styled.div(({ theme }) => ({
    margin: `0 ${theme.spacing.medium}`,
}));

export const CashbackCallout = styled.div(({ theme }) => ({
    paddingLeft: theme.spacing.large,
    marginTop: theme.spacing.xSmall,
    color: theme.colors[theme.colorMode].text_Low_Emphasis,
}));

export const BackIconWrapper = styled.div(({ theme }) => ({
    position: "absolute",
    left: theme.spacing.medium,
    display: "flex",
    alignSelf: "center",
}));

export const ShareIconWrapper = styled.div({});

export const HelpIconWrapper = styled.div(({ theme }) => ({
    display: "flex",
    border: `1px solid ${HELP_ICON_BORDER_COLOR}`,
    padding: `${theme.spacing.xxSmall} ${theme.spacing.xSmall}`,
    marginLeft: theme.spacing.xSmall,
    borderRadius: theme.cornerRadius.xxSmall,
    gap: theme.spacing.xxSmall,
    alignItems: "center",
}));

export const HeaderWrapper = styled.div<IHeaderWrapperProps>(({ theme, isScrolled }) => ({
    display: "flex",
    justifyContent: "space-between",
    position: "fixed",
    top: 0,
    left: 0,
    padding: theme.spacing.large,
    width: "100%",
    height: HEADER_HEIGHT,
    background: isScrolled ? theme.colors[theme.colorMode].background_Primary : "transparent",
    boxShadow: isScrolled ? theme.elevation[theme.colorMode].skim.boxShadow : "none",
    transition: "background-color 0.3s ease-in-out",
    zIndex: 3,

    "@supports (padding-top: var(--sat, 0px))": {
        paddingTop: `calc(var(--sat, 0px) + ${theme.spacing.large})`,
        height: `calc(var(--sat, 0px) + ${HEADER_HEIGHT})`,
        minHeight: `calc(var(--sat, 0px) + ${HEADER_HEIGHT})`,
    },

    [`@media (min-width: ${DEVICE_DIMENSIONS.DESKTOP_MIN})`]: {
        maxWidth: TABLET_MAX_WIDTH,
        left: "50%",
        transform: "translateX(-50%)",
    },
}));

export const HeaderRightSectionWrapper = styled.div(({ theme }) => ({
    display: "flex",
    gap: theme.spacing.small,
    position: "absolute",
    right: theme.spacing.medium,
    alignItems: "center",
    marginTop: `calc(-1 * ${theme.spacing.xxSmall})`,
}));

export const ViewRestaurantCtaWrapper = styled.div(({ theme }) => ({
    padding: `${theme.spacing.large} 0px`,
    margin: `0 ${theme.spacing.medium}`,
}));

export const CommsTextBannerWrapper = styled.div(({ theme }) => ({
    margin: `0px ${theme.spacing.medium} ${theme.spacing.large}`,
}));

export const ToastContainer = styled.div(({ theme }) => ({
    [`& .${TOAST_CONTAINER_CLASSNAME}`]: {
        bottom: TOAST_BOTTOM_MARGIN,
        left: theme.spacing.medium,
        right: theme.spacing.medium,
        marginLeft: "auto",
        marginRight: "auto",
        width: "fit-content",
        maxWidth: `calc(100% - 2 * ${theme.spacing.medium})`,
        zIndex: 2,
        [`@media (min-width: ${DEVICE_DIMENSIONS.DESKTOP_MIN})`]: {
            maxWidth: TABLET_MAX_WIDTH,
        },
    },
}));

export const DividerWrapper = styled.div(({ theme }) => ({
    width: "100vw",
    height: theme.spacing.xSmall,
    margin: `${theme.spacing.large} 0`,
    backgroundColor: theme.colors[theme.colorMode].background_Secondary_Alpha,
}));

export const LoadingContainer = styled.div({
    height: "100vh",
    flexDirection: "column",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

export const HalfCardPortalContainer = styled.div<{ showPassDetailsCard: boolean }>(
    ({ showPassDetailsCard, theme }) => ({
        [`@media (min-width: ${DEVICE_DIMENSIONS.DESKTOP_MIN})`]: {
            display: showPassDetailsCard ? "block" : "none",
            position: "fixed",
            width: "100%",
            height: "100%",
            bottom: 0,
            left: "50%",
            maxWidth: TABLET_MAX_WIDTH,
            transform: "translateX(-50%)",
            zIndex: theme.elevation[theme.colorMode].skim.zIndex,
        },
    }),
);

export const HalfCardPortalContainerWrapper = styled(HalfCardPortalContainer)(() => ({
    "div div div div button": {
        zIndex: 100,
    },
}));
